import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';

export function ServiceWorkerUpdater() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Check for updates on mount
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Check for waiting service worker (new version ready)
        if (reg.waiting) {
          setShowUpdate(true);
        }
        
        // Listen for new service worker installations
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                setShowUpdate(true);
              }
            });
          }
        });
      });

      // Listen for controller change (after skipWaiting)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });

      // Check for updates every 60 seconds
      const interval = setInterval(() => {
        navigator.serviceWorker.ready.then((reg) => {
          reg.update();
        });
      }, 60000);

      return () => clearInterval(interval);
    }
  }, []);

  const handleUpdate = () => {
    if (registration && registration.waiting) {
      // Tell the waiting service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback: clear caches and reload
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
      window.location.reload(true);
    }
    setShowUpdate(false);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    // Show again after 5 minutes
    setTimeout(() => {
      if (registration?.waiting) {
        setShowUpdate(true);
      }
    }, 300000);
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-blue-600 text-white p-4 rounded-lg shadow-lg z-50 animate-in slide-in-from-bottom">
      <div className="flex items-start gap-3">
        <RefreshCw className="w-6 h-6 flex-shrink-0 animate-spin" />
        <div className="flex-1">
          <h4 className="font-semibold">New Version Available!</h4>
          <p className="text-sm text-blue-100 mt-1">
            A new version of the app is ready. Update now to get the latest features and fixes.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleUpdate}
              size="sm"
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              Update Now
            </Button>
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="text-white hover:bg-blue-700"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-blue-200 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default ServiceWorkerUpdater;
